import React, { useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';

import styles from './SicknessReports.module.scss';
import { useTranslate } from 'react-polyglot';
import { useAuth } from '../../../Auth';
import PageTable from '../../Shared/PageTable/PageTable';
import { useAxios } from '../../../AxiosHandler';
import { general } from '../../../general';

function SicknessReports(props: SicknessReportsProps) {
    const t = useTranslate();
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useAuth();
    const axiosHandler = useAxios();
    const axios = axiosHandler.axios;

    // Samme mønster som Parringer: fanen ligger i URL-en så den overlever
    // tilbakenavigering, med replace: true så fanebytter ikke fyller historikken.
    const tabKeys = ["new", "accepted", "rejected"];
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get("tab");
    const activeTab = tabParam != null && tabKeys.includes(tabParam) ? tabParam : "new";

    function setActiveTab(key: string) {
        const params = new URLSearchParams(searchParams);
        params.set("tab", key);
        setSearchParams(params, { replace: true });
    }

    const [schema, setSchema] = React.useState<any | undefined>();

    const fieldsInTable = [
        "TIMESTAMP",
        "dogName",
        "ownerName",
    ];

    function mapFieldToDataAnswer(field: string) {
        let fieldId = 0;
        switch (field) {
            case "dogName": fieldId = 1; break;
            case "ownerName": fieldId = 2; break;
        }
        return (data: any) => {
            return data.DATA.find((d: any) => d.TRAIT_ID == fieldId)?.answer;
        }
    }

    function createTraitColumn(trait: any) {
        if (trait != undefined) {
            let column: any = {
                label: t("sicknessreports.fields." + trait),
                field: trait,
                noFilter: true,
            };

            if (trait == "TIMESTAMP") {
                column["override"] = (data: any) => {
                    return new Date(data.TIMESTAMP).toLocaleString("no-NO");
                }
            }

            switch (trait) {
                case "dogName":
                case "ownerName":
                    column["override"] = mapFieldToDataAnswer(trait);
                    break;
            }

            return column;
        } else {
            return undefined;
        }
    }

    function createColumns() {
        let columns: any[] = [];
        fieldsInTable.forEach((trait: string) => {
            columns.push(createTraitColumn(trait));
        });
        
        return columns;
    }

    function getSchemaBySpecialKeyAndBreed() {
        axios.single({
            method: 'get',
            url: "/api/dynschema/special/" + auth.selectedBreed + "/sicknessreg/" + auth.selectedBreed,
            responseType: 'json',
            headers: { 'x-access-token': auth.user['token'], "selected-country": auth.selectedCountry || "all" }
        }).then((res: any) => {
            setSchema(res.data);
        }).catch((err: any) => {
            console.log(err);
        });
    }
            
    useEffect(() => {
        if (
            (
                auth.user?.userdata?.ROLE != "admin" &&
                auth.user?.userdata?.ROLE != "sysadmin" &&
                auth.user?.userdata?.ROLE != "user"
            ) ||
            general.getBreedAccess(auth.user, auth.selectedBreed) == "dogowner"
        ) {
            navigate("/");
        }
    }, [auth.user?.userdata?.ROLE, auth.selectedBreed]);

    useEffect(() => {
        if (auth.selectedBreed != undefined) {
            getSchemaBySpecialKeyAndBreed();
        }
    }, [auth.selectedBreed]);

    return (
        <div className="maincontent">
            <div className={styles.dogs}>
                <h1>{t("sicknessreports.title")}</h1>
                <p>
                    {t("sicknessreports.linktext")}:&nbsp;
                    <a
                        href={window.location.origin + `/skjema/dyn/${auth.selectedBreed}/${schema?.ID}`}
                        target="_blank"
                    >
                        {window.location.origin + `/skjema/dyn/${auth.selectedBreed}/${schema?.ID}`}
                    </a>
                </p>
                <div className={styles.tabs}>
                    <div className={styles.tabList}>
                        {[
                            { key: "new",      label: t("sicknessreports.filters.status.new") },
                            { key: "accepted", label: t("sicknessreports.filters.status.accepted") },
                            { key: "rejected", label: t("sicknessreports.filters.status.rejected") },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className={styles.tabUnderline} />
                </div>
                { auth.selectedBreed != undefined &&
                    <PageTable
                        dataUrl={"/api/schema/getAllPaged/" + auth.selectedBreed}
                        saveFiltersKey={"sicknessreports.table." + auth.selectedBreed}
                        onLineClick={(dataLine: any) => {
                            // Ta med fanen inn i detaljsida, så tilbake-knappen der
                            // treffer riktig fane.
                            navigate("/sykdomsrapporter/" + dataLine.SCHEMA_KEY + "/" + dataLine.ID, {
                                state: { listSearch: location.search },
                            });
                        }}
                        options={{
                            showFields: [
                                ...createColumns(),
                            ],
                        }}
                        permaFilters={[
                            {
                                field: "BREED_ID",
                                exact: false,
                                type: "and",
                                values: [auth.selectedBreed?.toString() || ""]
                            },
                            {
                                field: "STATUS",
                                exact: false,
                                type: "and",
                                values: [activeTab]
                            },
                            {
                                field: "SCHEMA_KEY",
                                exact: false,
                                type: "and",
                                values: ["sicknessreg"]
                            }
                        ]}
                        selectedTraits={fieldsInTable}
                        clickable
                        defaultSortDirection="DESC"
                        defaultSortField="TIMESTAMP"
                        hidePagination={false}
                        hidePagesize={false}
                        hideFilter={false}
                    />
                }
            </div>
        </div>
    );
}

class SicknessReportsProps {
    
}

export default SicknessReports;